function createPaymentWallet() {
    'use strict';

    const ALLOWED_NETWORKS = ['VISA', 'MASTERCARD', 'AMEX', 'DISCOVER'];
    const APPLE_NETWORKS    = ['visa', 'masterCard', 'amex', 'discover'];
    const APPLE_PAY_SDK_URL = 'https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js';

    function resolveAmount(amount) {
        const value = typeof amount === 'function' ? amount() : amount;
        return parseFloat(value) || 0;
    }

    function revealWalletPill(methodId) {
        const pill = document.querySelector('[data-payment-method="' + methodId + '"]');
        if (pill) pill.style.display = '';

        const railModule = document.querySelector('[data-rail-method="' + methodId + '"]');
        if (railModule) railModule.hidden = false;

        const switcher = document.querySelector('.payment-method-switcher');
        if (!switcher) return;
        const visiblePills = switcher.querySelectorAll('.payment-method-pill:not([style*="display:none"])').length;
        switcher.style.display = visiblePills > 1 ? '' : 'none';
    }

    function loadScript(src) {
        return new Promise(function (resolve, reject) {
            const existing = document.querySelector('script[src="' + src + '"]');
            if (existing) {
                if (existing.dataset.loaded === 'true') {
                    resolve();
                    return;
                }
                existing.addEventListener('load', resolve);
                existing.addEventListener('error', reject);
                return;
            }
            const s = document.createElement('script');
            s.src = src;
            s.async = false;
            s.onload = function () {
                s.dataset.loaded = 'true';
                resolve();
            };
            s.onerror = reject;
            document.head.appendChild(s);
        });
    }

    /**
     * Google Pay
     *
     * @param {Object} gpConfig  - { merchantId, gatewayMerchantId, environment }
     * @param {number|Function} amount - charge amount or a function returning it
     * @param {string} currency  - e.g. 'USD'
     * @param {Function} onToken - called with { payment_key, bin_type, wallet_source }
     * @param {Function} onError - called with error message string
     */
    function initGooglePay(gpConfig, amount, currency, onToken, onError) {
        loadScript('https://pay.google.com/gp/p/js/pay.js').then(function () {
            if (typeof google === 'undefined' || !google.payments) {
                onError('Google Pay SDK failed to load.');
                return;
            }

            const client = new google.payments.api.PaymentsClient({
                environment: gpConfig.environment || 'TEST',
            });

            const baseRequest = {
                apiVersion: 2,
                apiVersionMinor: 0,
            };

            const tokenizationSpec = {
                type: 'PAYMENT_GATEWAY',
                parameters: {
                    gateway: 'acceptblue',
                    gatewayMerchantId: gpConfig.gatewayMerchantId,
                },
            };

            const cardPaymentMethod = {
                type: 'CARD',
                parameters: {
                    allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
                    allowedCardNetworks: ALLOWED_NETWORKS,
                },
                tokenizationSpecification: tokenizationSpec,
            };

            const isReadyRequest = Object.assign({}, baseRequest, {
                allowedPaymentMethods: [cardPaymentMethod],
            });

            client.isReadyToPay(isReadyRequest).then(function (response) {
                if (!response.result) {
                    return;
                }

                revealWalletPill('googlepay');

                const button = client.createButton({
                    buttonColor: 'black',
                    buttonType: 'pay',
                    buttonSizeMode: 'fill',
                    onClick: function () {
                        const paymentDataRequest = Object.assign({}, baseRequest, {
                            allowedPaymentMethods: [cardPaymentMethod],
                            transactionInfo: {
                                totalPriceStatus: 'FINAL',
                                totalPrice: resolveAmount(amount).toFixed(2),
                                currencyCode: currency || 'USD',
                                countryCode: 'US',
                            },
                            merchantInfo: {
                                merchantId: gpConfig.merchantId,
                                merchantName: document.title || 'Donation',
                            },
                        });

                        client.loadPaymentData(paymentDataRequest).then(function (paymentData) {
                            const token = paymentData.paymentMethodData.tokenizationData.token;
                            const fundingSource = paymentData.paymentMethodData.info
                                ? paymentData.paymentMethodData.info.cardFundingSource
                                : null;
                            const binType = fundingSource === 'CREDIT' ? 'C'
                                : (fundingSource === 'DEBIT' || fundingSource === 'PREPAID') ? 'D'
                                : null;

                            onToken({ payment_key: token, bin_type: binType, wallet_source: 'googlepay' });
                        }).catch(function (err) {
                            if (err && err.statusCode !== 'CANCELED') {
                                onError('Google Pay was not completed. Please try again.');
                            }
                        });
                    },
                });

                const wrap = document.getElementById('googlePayButton');
                if (wrap) {
                    wrap.innerHTML = '';
                    wrap.appendChild(button);
                }
            }).catch(function () {
                onError('Unable to initialize Google Pay.');
            });
        }).catch(function () {
            onError('Failed to load Google Pay SDK.');
        });
    }

    /**
     * Apple Pay
     *
     * @param {Object} apConfig  - { publicApiKey, apiBase }
     * @param {number|Function} amount - charge amount or a function returning it
     * @param {string} currency  - e.g. 'USD'
     * @param {Function} onToken - called with { payment_key, bin_type, wallet_source }
     * @param {Function} onError - called with error message string
     * @param {Function} onComplete - called after session.completePayment (success/fail); receives bool
     */
    function initApplePay(apConfig, amount, currency, onToken, onError, onComplete) {
        loadScript(APPLE_PAY_SDK_URL).then(function () {
            setupApplePayButton(apConfig, amount, currency, onToken, onError, onComplete);
        }).catch(function () {
            onError('Failed to load Apple Pay SDK.');
        });
    }

    function setupApplePayButton(apConfig, amount, currency, onToken, onError, onComplete) {
        const btnWrap = document.getElementById('applePayButton');

        if (typeof ApplePaySession === 'undefined' || !ApplePaySession.canMakePayments()) {
            return;
        }

        revealWalletPill('applepay');
        if (btnWrap) btnWrap.style.display = '';

        const btnContainer = btnWrap || document.getElementById('applePayPanel');
        if (!btnContainer) return;

        const button = btnContainer.querySelector('apple-pay-button') || btnContainer;

        let sessionActive = false;
        function lockButton() {
            button.style.pointerEvents = 'none';
        }
        function unlockButton() {
            sessionActive = false;
            button.style.pointerEvents = '';
        }

        if (typeof button.__applePayClick === 'function') {
            button.removeEventListener('click', button.__applePayClick);
        }

        function handleApplePayClick() {
            if (sessionActive) return;
            sessionActive = true;
            lockButton();

            const request = {
                countryCode: 'US',
                currencyCode: currency || 'USD',
                supportedNetworks: APPLE_NETWORKS,
                merchantCapabilities: ['supports3DS'],
                total: {
                    label: document.title || 'Donation',
                    amount: resolveAmount(amount).toFixed(2),
                },
            };

            let session;
            try {
                session = new ApplePaySession(3, request);
            } catch (e) {
                unlockButton();
                onError('Unable to start Apple Pay session.');
                return;
            }

            session.onvalidatemerchant = function (event) {
                const authHeader = 'Basic ' + btoa(apConfig.publicApiKey + ':');
                fetch(apConfig.apiBase + 'apple-pay/session', {
                    method: 'POST',
                    headers: {
                        'Authorization': authHeader,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ validation_url: event.validationURL }),
                }).then(function (res) {
                    if (!res.ok) throw new Error('Merchant validation failed');
                    return res.json();
                }).then(function (merchantSession) {
                    session.completeMerchantValidation(merchantSession);
                }).catch(function () {
                    session.abort();
                    unlockButton();
                    onError('Apple Pay merchant validation failed. Please try again.');
                });
            };

            session.onpaymentauthorized = function (event) {
                const paymentData = JSON.stringify(event.payment.token.paymentData);
                const methodType  = event.payment.token.paymentMethod
                    ? event.payment.token.paymentMethod.type
                    : '';
                const binType = methodType === 'credit' ? 'C'
                    : (methodType === 'debit' || methodType === 'prepaid') ? 'D'
                    : null;

                onToken(
                    { payment_key: paymentData, bin_type: binType, wallet_source: 'applepay' },
                    function (success) {
                        session.completePayment(
                            success
                                ? ApplePaySession.STATUS_SUCCESS
                                : ApplePaySession.STATUS_FAILURE
                        );
                        if (success) {
                            sessionActive = false;
                        } else {
                            unlockButton();
                        }
                        if (typeof onComplete === 'function') onComplete(success);
                    }
                );
            };

            session.oncancel = function () {
                unlockButton();
            };
            session.begin();
        }

        button.__applePayClick = handleApplePayClick;
        button.addEventListener('click', handleApplePayClick);
        button.dataset.applePayBound = '1';
    }

    return { initGooglePay: initGooglePay, initApplePay: initApplePay };
}

const PaymentWallet = createPaymentWallet();

export default PaymentWallet;
